import {Action, Path} from "history";
import {observer} from "mobx-react-lite";
import React, {useEffect} from "react";
import {useAsync} from "react-async";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router";
import {ToastMessageStatus} from "../../../../components/atoms/toastMessage/toastMessage";
import CreateEditEventTemplateTemplate from "../../../../components/templates/eventCatalogue/createEditEventTemplateTemplate";
import {useStores} from "../../../../stores/useStores";
import {RoutePaths} from "../../../../shared/routePaths";
import {Navigation} from "../../../../utils/navigation";
import {runInAction} from "mobx";
import {QueryParams} from "../../../../shared/enums/queryParams";
import {eventCalendarTypeFromCatalogueParam} from "../../../../shared/eventCalendarTypeHelper";

const CreateEventTemplate = () => {
  const {
    uiStore: {
      modalStore,
      toastMessageStore,
      eventCatalogueUiStore,
      eventsUiStore
    },
    domainStore: {eventCatalogueDomainStore}
  } = useStores();

  const history = useHistory();
  const {t} = useTranslation(["translation", "eventTemplate"]);

  const loadEventCategories = useAsync({
    deferFn: eventCatalogueUiStore.loadEventCategories,
    onReject: (error) => modalStore.openErrorModal(error)
  });

  useEffect(
    () =>
      runInAction(() => {
        eventsUiStore.selectedCalendarType =
          eventCalendarTypeFromCatalogueParam(
            Navigation.getQueryParamByKey(history, QueryParams.type)
          );
        eventCatalogueDomainStore.eventTemplate.calendarType =
          eventsUiStore.selectedCalendarType;
        loadEventCategories.run();
      }),
    []
  );

  useEffect(() => {
    eventCatalogueUiStore.resetSearch();
  }, [eventCatalogueUiStore]);

  const onPublish = useAsync({
    deferFn: eventCatalogueDomainStore.createEventTemplate,
    onReject: () => {
      toastMessageStore.displayToastMessage(
        t("eventTemplate:messages.createFailure", {
          title: eventCatalogueDomainStore.eventTemplate.title.text
        }),
        ToastMessageStatus.Danger
      );
    },
    onResolve: (title) => {
      toastMessageStore.displayToastMessage(
        t("eventTemplate:messages.createSuccess", {title}),
        ToastMessageStatus.Success
      );
      Navigation.go(history, RoutePaths.EventTemplates);
    }
  });

  const onImageSelected = useAsync({
    deferFn: (args) =>
      eventCatalogueDomainStore.eventTemplate.image.setImageFromUpload(args[0])
  });

  React.useEffect(() => {
    const unblock = history.block((path: Path, action: Action) => {
      if (eventCatalogueDomainStore.eventTemplate.hasChanges) {
        // If the modal is open and the user hit the back button on the browser, unblock the navigation.
        // OR we are still on event template page i.e. changing selected village.
        if (
          (modalStore.isOpen && action === Action.Pop) ||
          path.pathname.includes(RoutePaths.EventTemplates)
        ) {
          unblock();
          return true;
        }
        modalStore.openModalWithContent({
          content: t("translation:messages.cancelCreateOrEdit", {
            target: t("eventTemplate:eventTemplate")
          }),
          title: t("translation:titles.unsavedChanges"),
          primaryButtonText: t("translation:confirmations.affirmativeLeave"),
          secondaryButtonText: t("translation:confirmations.negative"),
          centered: true,
          dangerButton: true,
          onPrimaryClick: () => {
            unblock();
            history.push(path);
            modalStore.toggle();
          },
          onSecondaryClick: modalStore.toggle
        });
        return false;
      }
    });
    return () => {
      unblock();
    };
  }, [
    eventCatalogueDomainStore.eventTemplate.hasChanges,
    history,
    modalStore,
    t
  ]);

  return (
    <CreateEditEventTemplateTemplate
      eventTemplate={eventCatalogueDomainStore.eventTemplate}
      categoriesLoading={loadEventCategories.isPending}
      imageLoading={false}
      isEditing={false}
      isLoading={false}
      isSaving={onPublish.isPending}
      onPublish={async () => {
        onPublish.run();
      }}
      onImageSelected={async (file) => await onImageSelected.run(file)}
      hasUnsavedChanges={eventCatalogueDomainStore.eventTemplate.hasChanges}
    />
  );
};

export default observer(CreateEventTemplate);
